import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import Image from "../common/Image";
import SEOComponent from "../common/seo";
import abaImage from "@/assets/images/aba-award.png";
import OwnersImage from "@/assets/images/about-us/owners-new.jpg";
import WorldImage from "@/assets/images/about-us/whoppah-world.png";
import ictMediaImage from "@/assets/images/ict-award.png";
import kpmgImage from "@/assets/images/kpmg-award.png";
import MtImage from "@/assets/images/mt-sprout-award.png";
import { BLOG_ROUTE } from "@/common/static/routes.static";
import { LokaliseMarkdownText } from "@/components/text/LokaliseMarkdownText";
import {
  JobsQuery_jobs_JobEntityResponseCollection_data_JobEntity as JobEntity,
  PublicationsQuery_publications_PublicationEntityResponseCollection_data_PublicationEntity as PublicationEntry,
} from "@/strapi/schema";
import { AnchorButton } from "@/ui/cva/Button";
import { Body } from "@/ui/typography/Body";
import DownloadIcon from "@whoppah/icons/24/Download";
import SparkIcon from "@whoppah/icons/24/Spark";
import { Heading } from "@whoppah/ui/cva/Heading";

/**
const Job = ({ job }: { job: JobEntity }) => {
  const { t } = useTranslation();

  return (
    <div key={job.id} className="flex w-full flex-col rounded-md">
      <div className="flex w-full flex-col bg-grey-50 px-4 py-2">
        <SparkIcon className="text-attention-500" />
        <Heading as="h5">{job.attributes?.title}</Heading>
      </div>
      <div className="flex h-full flex-col justify-between rounded-md border-4 border-grey-50 px-4 py-6 md:px-5">
        <Body as="p" className="mb-6">
          {job.attributes?.description}
        </Body>
        <div className="flex flex-col gap-x-4 gap-y-4 md:flex-row">
          <PopupButton
            as="span"
            hidden={{ role: job.attributes?.title ?? "" }}
            id="JmIbFvk1"
          >
            <Button className="w-full md:w-[11.25rem]" size="large">
              {t("about:about-apply-now")}
            </Button>
          </PopupButton>
          {job.attributes?.document?.data?.attributes?.url && (
            <LinkButton
              href={job.attributes.document.data.attributes.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-full md:w-auto"
              appearance="outlined"
              rightIcon={<DownloadIcon />}
              size="large"
            >
              {t("about:about-download-description")}
            </LinkButton>
          )}
        </div>
      </div>
    </div>
  );
};
*/

const Publication = ({ publication }: { publication: PublicationEntry }) => {
  const blogSlug = publication.attributes?.blog_post?.data?.attributes?.slug;

  return (
    <div className="flex flex-col space-y-4" key={publication.id}>
      <div className="relative h-[15.625rem] w-full md:h-[18.375rem]">
        <Image
          src={publication.attributes?.image?.data?.attributes?.url}
          alt="Publication image"
          layout="fill"
          className="object-cover"
        />
      </div>
      <span className="px-4 md:px-0">
        <Heading as="h5">{publication.attributes?.title}</Heading>
        <Link
          prefetch={false}
          href={blogSlug ? `${BLOG_ROUTE}/${blogSlug}` : BLOG_ROUTE}
          className="text-brand-500 underline"
        >Link</Link>
      </span>
    </div>
  );
};

export type AboutUsProps = {
  jobs?: JobEntity[];
  publications: PublicationEntry[];
};

export const AboutUs = ({ publications }: AboutUsProps) => {
  const { t } = useTranslation("about");

  const awards = [
    { title: "kmpg", image: kpmgImage },
    { title: "aba", image: abaImage },
    { title: "ict", image: ictMediaImage },
    { title: "mt-sprout", image: MtImage },
  ];

  return (
    <>
      <SEOComponent
        title={t("about-page-seo-title")}
        description={t("about-page-seo-description")}
      />
      <div className="md:px-auto mb-6 grid grid-cols-1 items-center justify-center gap-y-4 md:mt-6 md:gap-y-20 md:px-6 lg:px-12 xl:px-24">
        <section className="grid grid-cols-1 gap-x-8 md:grid-cols-3">
          <div className="order-2 col-span-1 flex flex-col justify-center space-y-4 px-4 md:order-none md:px-0">
            <span className="flex flex-col items-center md:items-start">
              <SparkIcon className="h-10 w-10 text-attention-500" />
              <Heading as="h1" size="h2" className="text-center md:text-left">
                {t("about:about-us-main-title")}
              </Heading>
            </span>
            <LokaliseMarkdownText
              lokaliseKey="about:about-us-whoppah-core"
              wrapper={Body}
              className="flex flex-col space-y-4"
            />
          </div>
          <div className="relative h-[15.625rem] md:order-none md:col-span-2 md:h-[31.25rem]">
            <Image
              className="object-cover object-top"
              src={OwnersImage}
              layout="fill"
              alt="Whoppah owners image"
            />
          </div>
        </section>
        <section className="grid grid-cols-1 gap-x-8 md:grid-cols-3">
          <div className="relative col-span-2 h-[15.625rem] md:h-[31.25rem]">
            <Image
              className="object-contain  md:object-cover"
              src={WorldImage}
              layout="fill"
              alt="World of Whoppah image"
            />
          </div>
          <div className="col-span-1 flex flex-col justify-center space-y-4 px-4 md:px-0">
            <Heading as="h3">{t("about:about-us-our-mission-title")}</Heading>
            <Body>{t("about:about-us-our-mission-desc")}</Body>
          </div>
        </section>
        <section className="flex flex-col space-y-6">
          <Heading as="h3" className="text-center">
            {t("about:about-our-awards")}
          </Heading>
          <div className="flex flex-wrap items-center justify-center space-x-4 space-y-4 px-4 md:justify-between md:space-y-0 md:px-6">
            {awards.map(award => (
              <div key={award.title} className="relative h-[3.125rem] w-40">
                <Image
                  src={award.image}
                  alt={award.title + " image"}
                  layout="fill"
                  objectFit="contain"
                />
              </div>
            ))}
          </div>
        </section>
        <section className="grid grid-cols-1 gap-y-6">
          <Heading as="h3" className="text-center">
            {t("about:about-publications-title")}
          </Heading>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {!!publications.length &&
              publications.map(publication => (
                <Publication key={publication.id} publication={publication} />
              ))}
          </div>
        </section>
        <section className="flex flex-col items-center space-y-6">
          <Heading as="h3"> {t("about:about-us-press-kit-title")}</Heading>
          <Body className="text-center">
            {t("about:about-us-press-kit-desc")}
          </Body>
          <AnchorButton
            rightIcon={<DownloadIcon />}
            appearance="outlined"
            size="large"
            target="_blank"
            rel="noopener noreferrer nofollow"
            href="https://drive.google.com/drive/folders/1xPVtvynf7ZaB5ujuPOG14S1XG-kV0PTL?usp=sharing"
          >
            {t("about:about-us-press-kit-download")}
          </AnchorButton>
        </section>
      </div>
    </>
  );
};
